<template>
    <div class="text-ref">
        <el-select :model-value="def" size="small" clearable filterable :placeholder="defaultPlaceholder" class="full"
            @update:model-value="onDefault">
            <el-option v-for="t in options" :key="t.code" :label="t.label" :value="t.code"></el-option>
        </el-select>
        <div v-for="(v, vi) in variants" :key="v.id" class="variant">
            <div class="variant-head">
                <span class="variant-label">条件 {{ vi + 1 }}</span>
                <el-button type="text" size="mini" class="danger" @click="removeVariant(vi)">删除条件文案</el-button>
            </div>
            <div v-for="(c, ci) in v.conds" :key="c.id" class="cond-row">
                <span class="cond-word">{{ ci ? '且' : '当' }}</span>
                <el-select v-model="c.var" size="mini" class="cond-var" @change="onVarChange(c)">
                    <el-option v-for="o in varOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
                </el-select>
                <el-input v-if="c.var === CUSTOM" v-model.trim="c.name" size="mini" class="cond-name" placeholder="变量名"
                    @input="emitChange"></el-input>
                <span class="cond-word">=</span>
                <el-select v-if="c.var !== CUSTOM" v-model="c.value" size="mini" class="cond-value" @change="emitChange">
                    <el-option label="是" :value="true"></el-option>
                    <el-option label="否" :value="false"></el-option>
                </el-select>
                <el-input v-else v-model.trim="c.value" size="mini" class="cond-value" placeholder="值"
                    @input="emitChange"></el-input>
                <el-button v-if="v.conds.length > 1" type="text" size="mini" class="danger" @click="removeCond(v, ci)">移除</el-button>
            </div>
            <div class="cond-row">
                <el-button type="text" size="mini" @click="addCond(v)">+ 条件</el-button>
            </div>
            <div class="cond-row">
                <span class="cond-word">显示</span>
                <el-select v-model="v.text" size="small" filterable placeholder="请选择文案" class="full" @change="emitChange">
                    <el-option v-for="t in options" :key="t.code" :label="t.label" :value="t.code"></el-option>
                </el-select>
            </div>
        </div>
        <div class="footer">
            <el-button type="text" size="mini" @click="addVariant">+ 添加条件文案</el-button>
            <span v-if="hasCustom" class="tip">自定义条件：值填 true/false 按布尔、纯数字按数字比较；App 不认识的条件会被忽略</span>
            <span v-else-if="variants.length" class="tip">按顺序取第一条全部满足的条件文案，都不满足时用默认文案</span>
        </div>
        <div v-if="error" class="error-tip">{{ error }}</div>
    </div>
</template>

<script>
import pairingApi from './api'
import { textOptionLabel, TEXT_REF_VARS, textRefDefault, textRefVariants, validateTextRef } from './utils'

const CUSTOM = '__custom'
let seq = 0

// 自定义条件的值：true/false 按布尔，纯数字按数字，其余按字符串
function parseValue(s) {
    if (s === 'true') return true
    if (s === 'false') return false
    if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s)
    return s
}

// 编辑一个文案引用（TextRef）：只有默认文案时输出字符串，有条件文案时输出 { default, variants }
export default {
    name: 'PairingTextRefSelect',
    props: {
        modelValue: { type: [String, Object], default: '' },
        // 文案用途
        type: { type: String, required: true },
        // 该用途下的文案列表；不传时组件自己加载
        texts: { type: Array, default: null },
        defaultPlaceholder: { type: String, default: '默认文案：不使用' },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            CUSTOM,
            def: '',
            variants: [],
            loadedTexts: [],
            lastEmitted: undefined,
        }
    },
    computed: {
        varOptions() {
            return [...TEXT_REF_VARS, { value: CUSTOM, label: '自定义…' }]
        },
        sourceTexts() {
            return this.texts || this.loadedTexts
        },
        // 当前引用的文案不在文案库里时也保留一项，避免下拉显示成空白
        options() {
            const options = this.sourceTexts.map((t) => ({ code: t.code, label: textOptionLabel(t) }))
            const used = [this.def, ...this.variants.map((v) => v.text)].filter(Boolean)
            used.forEach((code) => {
                if (!options.some((o) => o.code === code)) options.unshift({ code, label: `${code}（不在文案库中）` })
            })
            return options
        },
        hasCustom() {
            return this.variants.some((v) => v.conds.some((c) => c.var === CUSTOM))
        },
        error() {
            return validateTextRef(this.build())
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(value) {
                // 自己刚发出的值不回填，避免输入中的自定义变量名被重排
                if (JSON.stringify(value) === JSON.stringify(this.lastEmitted)) return
                this.parse(value)
            },
        },
        type: {
            immediate: true,
            handler() {
                if (!this.texts) this.loadTexts()
            },
        },
    },
    methods: {
        loadTexts() {
            const type = this.type
            pairingApi.TextList({ type }).then((res) => {
                if (type === this.type) this.loadedTexts = res.data.data || []
            }).catch(() => { })
        },
        parse(value) {
            this.def = textRefDefault(value)
            this.variants = textRefVariants(value).map((v) => ({
                id: ++seq,
                text: v.text || '',
                conds: Object.keys(v.when || {}).map((k) => {
                    const builtin = TEXT_REF_VARS.some((x) => x.value === k) && typeof v.when[k] === 'boolean'
                    return builtin
                        ? { id: ++seq, var: k, name: '', value: v.when[k] }
                        : { id: ++seq, var: CUSTOM, name: k, value: String(v.when[k]) }
                }),
            }))
        },
        build() {
            if (!this.variants.length) return this.def || ''
            return {
                default: this.def || '',
                variants: this.variants.map((v) => {
                    const when = {}
                    v.conds.forEach((c) => {
                        if (c.var === CUSTOM) {
                            if (c.name) when[c.name] = parseValue(c.value)
                        } else {
                            when[c.var] = c.value
                        }
                    })
                    return { when, text: v.text || '' }
                }),
            }
        },
        emitChange() {
            const value = this.build()
            this.lastEmitted = value
            this.$emit('update:modelValue', value)
        },
        onDefault(code) {
            this.def = code || ''
            this.emitChange()
        },
        newCond() {
            return { id: ++seq, var: TEXT_REF_VARS[0].value, name: '', value: true }
        },
        addVariant() {
            this.variants.push({ id: ++seq, text: '', conds: [this.newCond()] })
            this.emitChange()
        },
        removeVariant(index) {
            this.variants.splice(index, 1)
            this.emitChange()
        },
        addCond(v) {
            const used = v.conds.map((c) => c.var)
            const free = TEXT_REF_VARS.find((x) => !used.includes(x.value))
            v.conds.push(free ? { id: ++seq, var: free.value, name: '', value: true } : { id: ++seq, var: CUSTOM, name: '', value: '' })
            this.emitChange()
        },
        removeCond(v, index) {
            v.conds.splice(index, 1)
            this.emitChange()
        },
        // 切换变量时把值换成对应类型：内置变量为布尔，自定义为字符串
        onVarChange(c) {
            if (c.var === CUSTOM) {
                c.value = ''
            } else {
                c.name = ''
                if (typeof c.value !== 'boolean') c.value = true
            }
            this.emitChange()
        },
    },
}
</script>

<style scoped>
.text-ref {
    width: 100%;
}

.full {
    width: 100%;
}

.variant {
    margin-top: 6px;
    padding: 4px 8px 6px;
    background: #f7f8fa;
    border-radius: 3px;
}

.variant-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: 24px;
}

.variant-label {
    font-size: 12px;
    color: #606266;
}

.cond-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
}

.cond-word {
    flex-shrink: 0;
    font-size: 12px;
    color: #909399;
}

.cond-var {
    width: 110px;
    flex-shrink: 0;
}

.cond-name {
    width: 120px;
    flex-shrink: 0;
}

.cond-value {
    width: 90px;
    flex-shrink: 0;
}

.footer {
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 22px;
}

.tip {
    font-size: 12px;
    color: #909399;
    line-height: 16px;
}

.error-tip {
    font-size: 12px;
    color: #f56c6c;
    line-height: 16px;
}

.danger {
    color: #f56c6c;
}
</style>
